import { useEffect, useRef, useState } from "react";

const NOT_HYDRATED = Symbol("not-hydrated");

function emptyGroups(groups) {
    return Object.fromEntries(groups.map((group) => [group.key, []]));
}

function idSetsEqual(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    const rightIds = new Set(right);
    return left.every((id) => rightIds.has(id));
}

/**
 * Stages a phase's module-group edits locally until an explicit save — shared by `PhaseForm.js`
 * (a create-or-existing phase) and `StudyPhasesList.js` (an existing phase's quick-edit view), so
 * neither commits an assign/unassign to the API until its own Save is clicked.
 * @param {Array} groups `MODULE_GROUPS`.
 * @param {object|null} hydrateFrom `{ [group.key]: module[] }` to seed from, or `null` while not
 *   ready to hydrate yet (e.g. the source query is still loading).
 * @param {string|number} [resetKey] Identifies which phase the staged state belongs to. Hydration
 *   only runs once per `resetKey`, so it won't clobber in-progress local edits on an unrelated
 *   background refetch of the same phase — changing it (a different phase, or create → saved)
 *   allows hydration to run again.
 * @param {(module: object) => (string|number)} getModuleId Resolves a module row's real id — the
 *   two callers' rows disagree on where that lives (see `getStudyPathModuleId`).
 */
export function useStagedPhaseModules({ groups, hydrateFrom, resetKey, getModuleId }) {
    const [stagedModules, setStagedModules] = useState(() => emptyGroups(groups));
    const [isHydrated, setIsHydrated] = useState(false);
    const baselineRef = useRef(emptyGroups(groups));
    const hydratedKeyRef = useRef(NOT_HYDRATED);

    useEffect(() => {
        if (hydrateFrom == null || hydratedKeyRef.current === resetKey) {
            return;
        }
        setStagedModules(hydrateFrom);
        baselineRef.current = hydrateFrom;
        hydratedKeyRef.current = resetKey;
        setIsHydrated(true);
    }, [hydrateFrom, resetKey]);

    const stageModule = (groupKey, module) => {
        setStagedModules((current) => (
            current[groupKey].some((staged) => getModuleId(staged) === getModuleId(module))
                ? current
                : { ...current, [groupKey]: [...current[groupKey], module] }
        ));
    };

    const unstageModule = (moduleId) => {
        setStagedModules((current) => Object.fromEntries(
            Object.entries(current).map(([key, staged]) => [key, staged.filter((entry) => getModuleId(entry) !== moduleId)])
        ));
    };

    const resetToBaseline = () => setStagedModules(baselineRef.current);

    const commitBaseline = (next = stagedModules) => {
        baselineRef.current = next;
    };

    const isDirty = isHydrated && groups.some((group) => (
        !idSetsEqual(stagedModules[group.key].map(getModuleId), baselineRef.current[group.key].map(getModuleId))
    ));

    return { stagedModules, stageModule, unstageModule, isDirty, isHydrated, resetToBaseline, commitBaseline };
}
